export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

export type WorkspaceSector = "energy" | "agriculture";

export interface Workspace {
  id: string;
  name: string;
  sector: WorkspaceSector;
}

const SKIP_REFRESH_RETRY = new Set(["/api/auth/login", "/api/auth/signup"]);

async function doFetch(path: string, options?: RequestInit): Promise<Response> {
  try {
    return await fetch(path, {
      ...options,
      headers: { "Content-Type": "application/json", ...(options?.headers ?? {}) },
    });
  } catch {
    // fetch itself threw — actual network failure, not an HTTP error response.
    // Keep this message generic and distinct from server-provided messages
    // below, per the spec's "network failure -> generic inline error" rule.
    throw new Error("Unable to reach the server. Please try again.");
  }
}

export class ApiError extends Error {
  constructor(message: string, public readonly body: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = "Something went wrong";
    let body: unknown;
    try {
      body = await res.json();
      if (typeof (body as { message?: unknown })?.message === "string") {
        message = (body as { message: string }).message;
      }
    } catch {
      // response wasn't JSON — fall back to the generic message above
    }
    throw new ApiError(message, body);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json();
}

async function requestWithRetry<T>(path: string, options: RequestInit | undefined, isRetry: boolean): Promise<T> {
  const res = await doFetch(path, options);

  if (res.status === 401 && !isRetry && !SKIP_REFRESH_RETRY.has(path)) {
    const refreshRes = await doFetch("/api/auth/refresh", { method: "POST" });
    if (refreshRes.ok) {
      return requestWithRetry<T>(path, options, true);
    }
    // The refresh itself failed — this is a real logout, not a retryable
    // error. Most call sites just render the thrown error inline, so redirect
    // here directly rather than relying on every page to do it themselves.
    // Guarded for the Node test environment, where `window` doesn't exist.
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
    throw new Error("Session expired. Please log in again.");
  }

  return parseResponse<T>(res);
}

export async function request<T>(path: string, options?: RequestInit): Promise<T> {
  return requestWithRetry<T>(path, options, false);
}

export interface SignupInput {
  workspaceName: string;
  sector: WorkspaceSector;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  turnstileToken: string;
}

export function signup(input: SignupInput) {
  return request<{ user: AuthUser; workspace: Workspace }>("/api/auth/signup", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export interface LoginInput {
  email: string;
  password: string;
  turnstileToken: string;
}

export function login(input: LoginInput) {
  return request<{ user: AuthUser }>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function logout() {
  return request<void>("/api/auth/logout", { method: "POST" });
}

export function getMe() {
  return request<{ user: AuthUser; workspace: Workspace | null }>("/api/auth/me");
}

export interface ChangePasswordInput {
  oldPassword: string;
  newPassword: string;
}

export function changePassword(input: ChangePasswordInput) {
  return request<{ message: string }>("/api/auth/password", {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export interface ForgotPasswordInput {
  email: string;
  turnstileToken: string;
}

export function forgotPassword(input: ForgotPasswordInput) {
  return request<{ message: string }>("/api/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export interface ResetPasswordInput {
  token: string;
  newPassword: string;
}

export function resetPassword(input: ResetPasswordInput) {
  return request<{ message: string }>("/api/auth/reset-password", {
    method: "POST",
    body: JSON.stringify(input),
  });
}
